// Port of companion/src/model.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250): the pure
// helpers only — sameId (:6), buildModel (:65-102), findBranch (:104-106), findStep (:108-110).
// loadFixture and DEFAULT_FIXTURE_PATH (:1-29, node:fs and a machine path) do not enter the core;
// the tests read the fixture through BASIL_FIXTURE_PATH (src/__tests__/support/fixture.ts).
// Outputs are the frozen ones byte for byte: the same key order, keys holding undefined kept,
// `price`/`gotoBranchId` and `check`/`switch`/`action` present only when the source carries them,
// `raw` the source step itself (not a copy).
import type {
  Branch,
  EngineId,
  Fixture,
  Model,
  ModelAnswer,
  ModelBranch,
  ModelEdge,
  ModelStep,
  Step,
} from '../../dorian-shared/types/episodeScript';

export const sameId = (left: unknown, right: unknown): boolean => String(left) === String(right);

/** A route target as a document carries it; model.mjs:32 drops null, undefined and ''. */
type EdgeTarget = EngineId | null | undefined;

// model.mjs:31-34. The caller builds the edge once the target is known to be routed, so each
// shape of the ModelEdge union is typed without a cast; the key order is the frozen one
// (from, to, kind, then the extras).
function pushEdge(edges: ModelEdge[], to: EdgeTarget, edge: (target: EngineId) => ModelEdge): void {
  if (to === null || to === undefined || to === '') return;
  edges.push(edge(to));
}

// model.mjs:36-63, in the frozen order: answers, check.gotoBranchId, switch arms, check.switch
// arms, hub options.
function collectStepEdges(edges: ModelEdge[], branch: Branch, step: Step): void {
  const from = branch.id;
  (step.answers ?? []).forEach((answer) => {
    pushEdge(edges, answer.gotoBranchId, (to) => ({
      from,
      to,
      kind: 'answer-goto',
      stepId: step.id,
      answerId: answer.id,
    }));
  });
  pushEdge(edges, step.check?.gotoBranchId, (to) => ({
    from,
    to,
    kind: 'check-arm',
    stepId: step.id,
  }));
  (step.switch ?? []).forEach((arm) => {
    pushEdge(edges, arm.gotoBranchId, (to) => ({
      from,
      to,
      kind: 'switch-arm',
      stepId: step.id,
      value: arm.value,
    }));
  });
  (step.check?.switch ?? []).forEach((arm) => {
    pushEdge(edges, arm.gotoBranchId, (to) => ({
      from,
      to,
      kind: 'check-arm',
      stepId: step.id,
      value: arm.value,
    }));
  });
  (step.hubOptions ?? []).forEach((option) => {
    pushEdge(edges, option.gotoBranchId, (to) => ({
      from,
      to,
      kind: 'hub-option-goto',
      stepId: step.id,
      optionId: option.id,
    }));
  });
}

export function buildModel(fixture: Fixture): Model {
  if (!fixture || !Array.isArray(fixture.branches)) {
    throw new TypeError('buildModel requires a fixture with branches[]');
  }
  const edges: ModelEdge[] = [];
  const branches = fixture.branches.map((branch): ModelBranch => {
    pushEdge(edges, branch.gotoBranchId, (to) => ({ from: branch.id, to, kind: 'branch-goto' }));
    const steps = (branch.steps ?? []).map((step): ModelStep => {
      collectStepEdges(edges, branch, step);
      return {
        id: step.id,
        stepTypeId: step.stepTypeId,
        text: step.text,
        characterId: step.characterId,
        characterExpressionId: step.characterExpressionId,
        answers: (step.answers ?? []).map((answer): ModelAnswer => ({
          id: answer.id,
          text: answer.text,
          ...(answer.answerPrice !== undefined ? { price: answer.answerPrice } : {}),
          ...(answer.gotoBranchId !== undefined ? { gotoBranchId: answer.gotoBranchId } : {}),
        })),
        ...(step.check !== undefined ? { check: step.check } : {}),
        ...(step.switch !== undefined ? { switch: step.switch } : {}),
        ...(step.action !== undefined ? { action: step.action } : {}),
        raw: step,
      };
    });
    return {
      id: branch.id,
      uuid: branch.uuid,
      title: branch.title,
      gotoBranchId: branch.gotoBranchId,
      locationId: branch.locationId,
      steps,
    };
  });
  return { branches, edges, serverTime: fixture.serverTime };
}

export function findBranch(fixture: Fixture, id: unknown): Branch | undefined {
  return fixture.branches.find((branch) => sameId(branch.id, id));
}

export function findStep(branch: Branch | null | undefined, id: unknown): Step | undefined {
  return branch?.steps?.find((step) => sameId(step.id, id));
}
